/**
 * taxes.ts — Federal, Illinois, and FICA tax calculations.
 *
 * All rates and thresholds sourced from facts.ts (HC-FACTS-IMMUTABLE).
 * Modeling assumptions: MFJ filing status, standard deduction applied,
 * no itemized deductions modeled (conservative assumption — actual tax
 * likely lower due to mortgage interest, state tax, charitable giving).
 */

import {
  FEDERAL_BRACKETS_MFJ,
  FEDERAL_STANDARD_DEDUCTION_MFJ,
  IL_TAX_RATE,
  SS_RATE,
  SS_WAGE_BASE,
  MEDICARE_RATE,
  MEDICARE_ADDITIONAL_RATE,
  MEDICARE_ADDITIONAL_THRESHOLD_MFJ,
  SE_TAX_RATE,
  SE_INCOME_FACTOR,
  SE_DEDUCTIBLE_FRACTION,
  RETIREMENT_401K_LIMIT,
  SS_MAX_TAXABLE_FRACTION,
} from './defaults'

export interface TaxResult {
  federalTax: number
  stateTax: number
  ficaTax: number
  totalTax: number
  effectiveRate: number
  agi: number
  taxableIncome: number
}

/**
 * Calculate federal income tax using progressive brackets on taxable income.
 * @param taxableIncome - Income after deductions
 * @returns Federal income tax owed
 */
export function federalIncomeTax(taxableIncome: number): number {
  if (taxableIncome <= 0) return 0
  let tax = 0
  for (const bracket of FEDERAL_BRACKETS_MFJ) {
    if (taxableIncome <= bracket.floor) break
    const taxableInBracket = Math.min(taxableIncome, bracket.ceiling) - bracket.floor
    tax += taxableInBracket * bracket.rate
  }
  return Math.max(0, tax)
}

/**
 * Calculate FICA taxes for W-2 wages (employee share only).
 * Separate from SE tax on business income.
 */
function ficaOnW2(w2Wages: number): number {
  const ss = Math.min(w2Wages, SS_WAGE_BASE) * SS_RATE
  const medicare = w2Wages * MEDICARE_RATE
  const additionalMedicare = Math.max(0, w2Wages - MEDICARE_ADDITIONAL_THRESHOLD_MFJ) * MEDICARE_ADDITIONAL_RATE
  return ss + medicare + additionalMedicare
}

/**
 * Calculate self-employment tax and the AGI deduction for it.
 */
function seTaxCalc(businessIncome: number): { seTax: number; seDeduction: number } {
  if (businessIncome <= 0) return { seTax: 0, seDeduction: 0 }
  const seBase = businessIncome * SE_INCOME_FACTOR
  const seTax = seBase * SE_TAX_RATE
  const seDeduction = seTax * SE_DEDUCTIBLE_FRACTION
  return { seTax, seDeduction }
}

/**
 * Compute total tax burden for a given income profile.
 *
 * @param w2Wages - Gross W-2 wages (salary + bonus + RSU vesting)
 * @param businessIncome - Net self-employment income
 * @param retirementContribution - Pre-tax 401k employee contribution (reduces AGI)
 * @param passiveIncome - Additional ordinary income NOT subject to SE tax
 *   (e.g. 312 Solutions loan interest, LLC distributions as passive investor)
 */
export function computeTaxes(
  w2Wages: number,
  businessIncome: number,
  retirementContribution: number,
  passiveIncome = 0,
): TaxResult {
  const { seTax, seDeduction } = seTaxCalc(businessIncome)

  // AGI: W-2 + SE income + passive income, minus SE deduction and 401k contribution
  const contribution = Math.min(retirementContribution, RETIREMENT_401K_LIMIT)
  const agi = w2Wages + businessIncome - seDeduction - contribution + passiveIncome

  // Taxable income after standard deduction
  const taxableIncome = Math.max(0, agi - FEDERAL_STANDARD_DEDUCTION_MFJ)

  const federalTax = federalIncomeTax(taxableIncome)
  const stateTax = agi * IL_TAX_RATE   // Illinois taxes AGI (no standard deduction)
  const ficaW2 = ficaOnW2(w2Wages)
  const ficaTax = ficaW2 + seTax

  const totalTax = federalTax + stateTax + ficaTax
  const grossIncome = w2Wages + businessIncome + passiveIncome
  const effectiveRate = grossIncome > 0 ? totalTax / grossIncome : 0

  return {
    federalTax,
    stateTax,
    ficaTax,
    totalTax,
    effectiveRate,
    agi,
    taxableIncome,
  }
}

/**
 * Marginal federal tax rate at a given taxable income level.
 * Used for estimating the tax impact of additional RSU vesting.
 */
export function marginalFederalRate(taxableIncome: number): number {
  for (let i = FEDERAL_BRACKETS_MFJ.length - 1; i >= 0; i--) {
    if (taxableIncome > FEDERAL_BRACKETS_MFJ[i].floor) {
      return FEDERAL_BRACKETS_MFJ[i].rate
    }
  }
  return FEDERAL_BRACKETS_MFJ[0].rate
}

/**
 * Compute taxes on retirement income (no W-2 wages).
 *
 * Retirement income sources:
 *  - Social Security benefits: 85% included in taxable income (MFJ combined income
 *    far exceeds $44k threshold at these SS benefit levels).
 *  - Business income: subject to SE tax + income tax.
 *  - passiveIncome: 312 Solutions distributions — ordinary income, no SE tax.
 *  - Retirement account withdrawals: taxable as ordinary income, but modeled
 *    separately at the portfolio level (conservative simplification).
 *
 * No FICA on SS benefits. SE tax on business income only.
 */
export function computeRetirementTaxes(
  ssIncome: number,        // total SS benefits received (user + spouse)
  businessIncome: number,  // self-employment income (real estate / business)
  passiveIncome = 0,       // 312 Solutions yield — ordinary income, no SE tax
): TaxResult {
  const { seTax, seDeduction } = seTaxCalc(businessIncome)

  // 85% of SS benefits are taxable at this income level (MFJ threshold $44k combined)
  const taxableSS = ssIncome * SS_MAX_TAXABLE_FRACTION

  const agi = taxableSS + businessIncome - seDeduction + passiveIncome
  const taxableIncome = Math.max(0, agi - FEDERAL_STANDARD_DEDUCTION_MFJ)

  const federalTax = federalIncomeTax(taxableIncome)
  const stateTax = agi * IL_TAX_RATE   // IL taxes AGI; SS partially exempt but 85% applies
  const ficaTax = seTax                // Only SE tax; no FICA on SS benefits

  const totalTax = federalTax + stateTax + ficaTax
  const grossIncome = ssIncome + businessIncome + passiveIncome
  const effectiveRate = grossIncome > 0 ? totalTax / grossIncome : 0

  return {
    federalTax,
    stateTax,
    ficaTax,
    totalTax,
    effectiveRate,
    agi,
    taxableIncome,
  }
}
